import { ChangeDetectorRef, Component, OnInit, signal, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Location, NgIf, NgFor, NgClass } from '@angular/common';
import { UntypedFormControl, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { merge, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { Project } from '../project';
import { ProjectService } from '../project.service';
import { User } from '../user';
import { AlertService } from '../alert.service';
import { NgbModal, NgbModalRef, NgbTooltip, NgbTypeahead, NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavContent, NgbNavOutlet, NgbDropdownItem, NgbDropdownButtonItem, NgbDropdownMenu, NgbDropdownToggle, NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { faAngleLeft, faEye, faSave, faTrashCan, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { UserService } from '../user.service';
import { FormatDateTimePipe } from '../format-date-time.pipe';
import { ProjectReviewsComponent } from '../project-reviews/project-reviews.component';
import { ProjectRemovalComponent } from '../project-removal/project-removal.component';
import { ProjectBasesComponent } from '../project-bases/project-bases.component';
import { ProjectInstallationComponent } from '../project-installation/project-installation.component';
import { ProjectPrepInventoryComponent } from '../project-prep-inventory/project-prep-inventory.component';
import { ProjectPrepOrdersComponent } from '../project-prep-orders/project-prep-orders.component';
import { ProjectPrepPendingComponent } from '../project-prep-pending/project-prep-pending.component';
import { ProjectInvoicesComponent } from '../project-invoices/project-invoices.component';
import { ProjectProposalComponent } from '../project-proposal/project-proposal.component';
import { NotesComponent } from '../notes/notes.component';
import { ProjectMockupsComponent } from '../project-mockups/project-mockups.component';
import { ProjectFilesComponent } from '../project-files/project-files.component';
import { ForbiddenValueDirective } from '../forbidden-value.directive';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { AdditionalProjectSettingsModalComponent } from '@app-shared/modals/additional-project-settings-modal/additional-project-settings-modal.component';
import { SettingService } from '@main/setting.service';
import { MultiYearAgreementService } from '@app-shared/services/multi-year-agreement.service';
import { AssignProjectToMyaModalComponent } from '@app-shared/modals/assign-project-to-mya-modal/assign-project-to-mya-modal.component';

@Component({
  selector: 'app-project-detail',
  templateUrl: './project-detail.component.html',
  styleUrls: ['./project-detail.component.css'],
  imports: [NgbTooltip, FaIconComponent, NgIf, RouterLink, ReactiveFormsModule, NgbTypeahead, ForbiddenValueDirective, NgFor, ProjectFilesComponent, ProjectMockupsComponent, NotesComponent, NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavContent, ProjectProposalComponent, NgClass, ProjectInvoicesComponent, ProjectPrepPendingComponent, ProjectPrepOrdersComponent, ProjectPrepInventoryComponent, NgbNavOutlet, ProjectInstallationComponent, ProjectBasesComponent, ProjectRemovalComponent, ProjectReviewsComponent, FormatDateTimePipe, NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, NgbDropdownButtonItem, NgbDropdownItem]
})
export class ProjectDetailComponent implements OnInit {

  // Modal
  private modalReference: NgbModalRef;
  @ViewChild('UNPAID_INVOICE_CONFIRMATION_MODAL', { static: false }) private unpaidInvoiceConfirmationModal: NgbModalRef;
  @ViewChild('MYA_CANCEL_LOST_STATUS_CONFIRMATION_MODAL', { static: false }) private myaCancelAndLostStatusConfirmationModal: NgbModalRef;

  // Properties
  project: Project;
  projectId: string;
  hasUnpaidInvoices: boolean = false;
  salesperson: User;
  createdBy: User;
  updatedBy: User;
  users: User[];
  activeTabId: number;
  projectSelectedStatus = signal<string>('IN PROGRESS');
  loginDetails: User;

  // Project Form
  projectForm: UntypedFormGroup;

  // Project Names
  projectNameTypeaheadInput: (text$: Observable<string>) => Observable<string[]>;
  projectNameTypeaheadInputFocus$ = new Subject<string>();

  // Project Categories
  projectCategoryTypeaheadInput: (text$: Observable<string>) => Observable<string[]>;
  projectCategoryTypeaheadInputFocus$ = new Subject<string>();

  // Project Storage Location
  projectStorageLocationTypeaheadInput: (text$: Observable<string>) => Observable<string[]>;
  projectStorageLocationTypeaheadInputFocus$ = new Subject<string>();

  // Font Awesome Properties
  faAngleLeft = faAngleLeft;
  faEye = faEye;
  faSave = faSave;
  faTrashCan = faTrashCan;
  faArrowRight = faArrowRight;

  constructor(private route: ActivatedRoute,
    private router: Router,
    private projectService: ProjectService,
    private alertService: AlertService,
    private modalService: NgbModal,
    private userService: UserService,
    public location: Location,
    private changeDetectorRef: ChangeDetectorRef,
    private multiYearAgreementService: MultiYearAgreementService) {
    this.route.paramMap.subscribe((res: any) => {
      this.projectId = res.params.projectId;
    });
  }

  ngOnInit(): void {
    this.loginDetails = JSON.parse(localStorage.getItem('login'));
    this.getListOfUsers();
    // this.projectId = this.route.snapshot.paramMap.get('projectId');
    this.projectForm = new UntypedFormGroup({
      name: new UntypedFormControl(),
      category: new UntypedFormControl(),
      year: new UntypedFormControl(),
      storageLocation: new UntypedFormControl(),
      stage: new UntypedFormControl('PROPOSAL'),
      status: new UntypedFormControl('IN PROGRESS'),
      salesperson: new UntypedFormControl(),
    });
    // Check for tab query parameter
    const queryParams = this.route.snapshot.queryParams;
    if (queryParams['tab'] === 'invoices') {
      this.activeTabId = 2; // Invoices tab
    }
    this.listenForProjectUpdates();
    this.getProject();
    this.prepareProjectNameTypeahead();
    this.prepareProjectCategoryTypeahead();
    this.prepareProjectStorageLocationTypeahead();
  }

  ngOnDestroy(): void {
    this.modalService.hasOpenModals() && this.modalService.dismissAll();
  }

  // Open Delete Project Confirmation Modal
  openDeleteProjectConfirmationModal(): void {
    const confirmationModalRef = this.modalService.open(ConfirmationModalComponent);
    confirmationModalRef.componentInstance.message = "Are you sure you would like to delete this project?";
    confirmationModalRef.componentInstance.subMessage = "This will also delete all files, notes, proposals and invoices under this project.";
    confirmationModalRef.componentInstance.actionBtnTitle = "Delete";
    confirmationModalRef.componentInstance.confirmed.subscribe(() => {
      this.deleteProject();
    });
  }

  // Open Unpaid Invoice Confirmation Modal
  openUnpaidInvoiceConfirmationModal(): void {
    this.modalReference = this.modalService.open(this.unpaidInvoiceConfirmationModal);
  }

  // Listen For Project Updates
  private listenForProjectUpdates(): void {
    this.projectService.projectUpdated.subscribe(() => {
      this.getProject();
      this.alertService.showSuccessAlert('Project Updated');
    });
  }

  // Get Project
  getProject(): void {
    this.projectService.getProject(this.projectId).subscribe((res) => {
      this.project = res.project;
      this.hasUnpaidInvoices = res.hasUnpaidInvoices;
      this.salesperson = res.salesperson;
      this.createdBy = res.createdBy;
      this.updatedBy = res.updatedBy;
      this.projectService.projectOptionalStages = this.project?.optionalStages;
      this.changeDetectorRef.detectChanges();
      this.setProjectFields();
      // Set active tab - use query param if set, otherwise use project stage
      if (!this.activeTabId) {
        this.activeTabId = this.getProjectStageNumber(this.project.stage);
      }
    });
  }

  // Set Project Fields
  private setProjectFields(): void {
    let login = JSON.parse(localStorage.getItem("login"))

    this.projectForm.controls.name.setValue(this.project.name);
    this.projectForm.controls.category.setValue(this.project.category);
    this.projectForm.controls.year.setValue(this.project.year);
    this.projectForm.controls.storageLocation.setValue(this.project.storageLocation);
    this.projectForm.controls.stage.setValue(this.project.stage);
    this.projectForm.controls.status.setValue(this.project.status);
    this.projectForm.controls.salesperson.setValue(this.salesperson.id);
    if (login && login.role == 'USER') this.projectForm.controls.salesperson.disable()
  }

  // Get Project Names
  private getProjectNames(term: string): Observable<string[]> {
    const params = {
      searchTerm: term,
      sortBy: 'project_name',
      sortDirection: 'ASC',
      limit: 10,
      offset: 0,
      column: 'project_name'
    };
    return this.projectService.getDistinctColumnValues(params);
  }

  // Prepare Project Name Typeahead
  private prepareProjectNameTypeahead(): void {
    this.projectNameTypeaheadInput = (text$: Observable<string>) => {
      const debouncedText$ = text$.pipe(debounceTime(250), distinctUntilChanged());
      return merge(debouncedText$, this.projectNameTypeaheadInputFocus$).pipe(switchMap((term) => {
        return this.getProjectNames((term.length == 0) ? null : term);
      }));
    }
  }

  // Get Project Categories
  private getProjectCategories(term: string): Observable<string[]> {
    const params = {
      searchTerm: term,
      sortBy: 'project_category',
      sortDirection: 'ASC',
      limit: 10,
      offset: 0,
      column: 'project_category'
    };
    return this.projectService.getDistinctColumnValues(params);
  }

  // Prepare Project Category Typeahead
  private prepareProjectCategoryTypeahead(): void {
    this.projectCategoryTypeaheadInput = (text$: Observable<string>) => {
      const debouncedText$ = text$.pipe(debounceTime(250), distinctUntilChanged());
      return merge(debouncedText$, this.projectCategoryTypeaheadInputFocus$).pipe(switchMap((term) => {
        return this.getProjectCategories((term.length == 0) ? null : term);
      }));
    }
  }

  // Get Project Storage Locations
  private getProjectStorageLocations(term: string): Observable<string[]> {
    const params = {
      searchTerm: term,
      sortBy: 'project_category',
      sortDirection: 'ASC',
      limit: 10,
      offset: 0,
      column: 'project_storage_location',
      hideBlanks: true
    };
    return this.projectService.getDistinctColumnValues(params);
  }

  // Prepare Project Storage Location Typeahead
  private prepareProjectStorageLocationTypeahead(): void {
    this.projectStorageLocationTypeaheadInput = (text$: Observable<string>) => {
      const debouncedText$ = text$.pipe(debounceTime(250), distinctUntilChanged());
      return merge(debouncedText$, this.projectStorageLocationTypeaheadInputFocus$).pipe(switchMap((term) => {
        return this.getProjectStorageLocations((term.length == 0) ? null : term);
      }));
    }
  }

  // Update Project
  updateProject(): void {
    if (this.projectForm.valid) {
      const project = {
        id: this.projectId,
        name: this.projectForm.value.name,
        category: this.projectForm.value.category,
        year: this.projectForm.value.year,
        storageLocation: this.projectForm.value.storageLocation,
        salesperson: this.projectForm.value.salesperson || this.salesperson.id,
        stage: this.projectForm.value.stage,
        status: this.projectForm.value.status,
      };
      this.projectService.updateProject(project).subscribe(() => {
        this.alertService.showSuccessAlert('Project Updated');
        this.getProject();
      });
    } else {
      this.projectForm.markAllAsTouched();
    }
  }

  // Update Project Status
  updateProjectStatus(status: string, confirm: boolean = false): void {
    if (confirm && status == 'COMPLETED' && this.hasUnpaidInvoices) {
      this.openUnpaidInvoiceConfirmationModal();
      return;
    }

    const isPartOfMultiYearAgreement = this.project?.multiYearAgreement && this.project?.multiYearAgreement?.myaId;

    if (confirm && (status == 'CANCELED' || status == 'LOST') && isPartOfMultiYearAgreement) {
      this.openCancelLostConfirmationModal(status);
      return;
    }
    this.projectService.updateProjectStatus(this.project.id, status).subscribe(() => {
      this.projectService.projectUpdated.emit();
      if (this.modalReference) this.modalReference.close();
    });
  }

  openCancelLostConfirmationModal(status: string): void {
    this.projectSelectedStatus.set(status);
    this.modalReference = this.modalService.open(this.myaCancelAndLostStatusConfirmationModal);
  }

  // Revert Project Status
  revertProjectStatus(): void {
    this.projectForm.controls.status.setValue(this.project.status);
  }

  // Delete Project
  deleteProject(): void {
    this.projectService.deleteProject(this.projectId).subscribe(() => {
      this.router.navigateByUrl(`/projects`, { replaceUrl: true });
    });
  }

  // Update Project Stage
  updateProjectStage(): void {
    this.projectService.updateProjectStage(this.projectId, 'INSTALLATION').subscribe(() => {
      this.projectService.projectUpdated.emit();
    });
  }

  onListenOnCheckins(allCheckedIn: boolean) {
    if (allCheckedIn) this.getProject();
  }

  getListOfUsers() {
    this.userService.getUsers().subscribe(users => {
      this.users = users
    })
  }

  openAdditionalProjectSettingsModal() {
    const modalRef = this.modalService.open(AdditionalProjectSettingsModalComponent);
    modalRef.componentInstance.project = this.project;
    modalRef.componentInstance.okay.subscribe(() => {
      this.getProject();
    });
  }

  openAssignProjectToMyaModal() {
    const modalRef = this.modalService.open(AssignProjectToMyaModalComponent);
    const modal = modalRef.componentInstance as AssignProjectToMyaModalComponent;

    modal.clientId.set(this.project?.clientId ?? '');
    modal.siteId.set(this.project?.siteId ?? null);
    modal.projectId.set(this.project?.id ?? null);
    modal.acceptedProposal.set(this.project?.acceptedProposal ?? null);
    modal.selectedProjectYear.set(this.project?.year ?? null);
    modalRef.componentInstance.selectedProject = this.project;
    modal.type.set('PROJECT_DETAIL');

    modal.okay.subscribe(() => {
      this.getProject();
    });
  }


  // Cancel MYA
  cancelMya(): void {
    const payload = {
      status: 'CANCELED',
      startYear: this.project?.multiYearAgreement?.projectYear,
      length: this.project?.multiYearAgreement?.myaLength,
    }
    this.multiYearAgreementService.updateMultiYearAgreement(this.project?.multiYearAgreement?.myaId, payload).subscribe(() => {
      // Update the project status after canceling the multi-year agreement
      this.projectService.updateProjectStatus(this.project.id, this.projectSelectedStatus()).subscribe(() => {
        this.alertService.showSuccessAlert('Multi Year Agreement Canceled');
        this.projectService.projectUpdated.emit();
        if (this.modalReference) this.modalReference.close();
      });
    });
  }

  // Remove Project
  removeProject(): void {
    this.multiYearAgreementService.removeMultiYearProject(this.project?.multiYearAgreement?.myaId, this.project.id).subscribe(() => {
      // Update the project status after removing mya project
      this.projectService.updateProjectStatus(this.project.id, this.projectSelectedStatus()).subscribe(() => {
        this.alertService.showSuccessAlert('Project Removed');
        this.projectService.projectUpdated.emit();
        if (this.modalReference) this.modalReference.close();
      });
    });
  }

  removeProjectFromMyaModal() {
    const modalRef = this.modalService.open(ConfirmationModalComponent);
    modalRef.componentInstance.message = "Are you sure you want to remove this project from the Multi Year Agreement?";
    modalRef.componentInstance.actionBtnTitle = "Remove Project";
    modalRef.componentInstance.confirmed.subscribe(() => {
      this.removeProject();
    });
  }

  // Project Form Accessors
  get name() { return this.projectForm.controls.name; }
  get category() { return this.projectForm.controls.category; }
  get year() { return this.projectForm.controls.year; }
  get storageLocation() { return this.projectForm.controls.storageLocation; }
  get salespersonCTRL() { return this.projectForm.controls.salesperson; }

  /* ----- Helper Functions ----- */

  // Get Project Stage Number
  getProjectStageNumber(stage: string): number {
    switch (stage) {
      case 'PROPOSAL':
        return 1;
      case 'INVOICE':
        return 2;
      case 'PREPARATION':
        return 3;
      case 'INSTALLATION':
        return 4;
      case 'BASES':
        return 5;
      case 'REMOVAL':
        return 6;
      case 'REVIEWS':
        return 7;
      default:
        return 0;
    }
  }
}
